/**
 * نقشه‌ای که فقط **نشان می‌دهد** — خواهرِ `mapPicker.ts`، و عمداً جدا از آن.
 *
 * آن یکی یک نقطه می‌گیرد و برمی‌گرداند؛ این یکی صدها نقطه می‌گذارد و هیچ
 * چیزی برنمی‌گرداند. یکی کردنشان یعنی صفحه‌ای که هم نشانگرِ کشیدنی دارد هم
 * دایره‌های شمارشی، و هر تغییر در یکی باید نگرانِ آن یکی باشد. تگ‌های
 * Leaflet مشترک‌اند (`LEAFLET_TAGS`) تا نسخه‌شان دو تا نشود.
 *
 * ═══ چرا رشته‌ی HTML و نه یک کامپوننت ═══
 *
 * همان دلیلِ `mapPicker`: این‌طوری صفر وابستگیِ npm اضافه می‌شود. Leaflet از
 * CDN داخلِ یک `<iframe srcdoc sandbox="allow-scripts">` بار می‌شود، پس نه
 * به باندلِ اپ می‌چسبد و نه به نشستِ کاربر دسترسی دارد. کاشی‌ها همان
 * OpenStreetMapِ بی‌کلیدند.
 *
 * ⚠ برچسبِ نقطه‌ها **متنِ کاربر/کرالر** است (نامِ مکان، آدرس). هر چیزی که
 * از این‌جا وارد صفحه می‌شود باید escape شود و می‌شود — هم موقعِ ساختنِ
 * رشته (`</script>`)، هم داخلِ خودِ صفحه موقعِ ساختنِ پاپ‌آپ.
 */
import { WEB_PALETTES } from '@palsandplans/tokens';
import { LEAFLET_TAGS, usablePoint, type PickedPoint } from './mapPicker.ts';

export interface MapViewPoint extends PickedPoint {
  /** خطِ اولِ پاپ‌آپ — معمولاً نامِ مکان. */
  title: string;
  /** خطِ دوم — معمولاً آدرس. */
  note?: string | null;
  /** چند رویداد روی همین نقطه؛ اندازه‌ی دایره از همین می‌آید. */
  events: number;
  /** مشکوک؟ قرمز می‌شود. «دور از مرکزِ شهر» تنها مصرفِ فعلی‌اش است. */
  suspect?: boolean;
}

export interface MapViewColors {
  dot: string;
  suspect: string;
}

/** ادمین همیشه تیره است (`ThemeGate`)، پس پیش‌فرض از همان پالت می‌آید. */
const DEFAULT_COLORS: MapViewColors = {
  dot: WEB_PALETTES.dark.brand,
  suspect: WEB_PALETTES.dark.danger,
};

/**
 * شعاعِ دایره از شمارِ رویداد.
 *
 * ریشه‌ی دوم و نه خودِ عدد: مساحتِ دایره با مربعِ شعاع می‌رود، پس شعاعِ
 * خطی یعنی نقطه‌ای با ۱۵۱ رویداد ۱۵۱ برابرِ **مساحت** نمی‌گیرد، بلکه
 * ۲۲۸۰۱ برابر — یک لکه که نصفِ شهر را می‌پوشاند. سقف هم دارد، چون روی
 * دادهٔ واقعی فاصله‌ی کوچک‌ترین و بزرگ‌ترین نقطه سه رقم است.
 */
export const dotRadius = (events: number): number =>
  Math.round(Math.min(20, 4 + Math.sqrt(Math.max(events, 1)) * 1.5));

/** متنی که قرار است داخلِ HTML بنشیند. */
const esc = (s: string) => s
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/**
 * JSON که قرار است داخلِ `<script>` بنشیند.
 *
 * ⚠ `<` جایگزین می‌شود و این اجباری است نه احتیاطِ اضافه: یک نامِ مکان که
 * `</script>` داشته باشد بی این، از تگ بیرون می‌زند و بقیه‌ی صفحه را
 * خراب می‌کند. `<` داخلِ رشته‌ی JSON همان `<` است، پس معنی عوض نمی‌شود.
 */
const safeJson = (v: unknown) => JSON.stringify(v).replace(/</g, '\\u003c');

/**
 * صفحه‌ی نقشه با همه‌ی نقطه‌ها.
 *
 * نقطه‌های بی‌معنی (NaN، صفر-صفر، بیرونِ بازه‌ی مرکاتور) همین‌جا می‌ریزند
 * بیرون — همان `usablePoint`ی که فرمِ انتخابِ مکان به کار می‌برد. بی آن، یک
 * ردیفِ خرابِ دیتابیس کلِ `fitBounds` را به وسطِ اقیانوس می‌برد و بقیه‌ی
 * نقطه‌ها نقطه‌ی ذره‌بینی می‌شدند.
 */
export function mapViewHtml(
  points: MapViewPoint[],
  colors: MapViewColors = DEFAULT_COLORS,
): string {
  const usable = points.filter((p) => usablePoint(p));

  const body = usable.length === 0
    ? '<div class="empty">هیچ نقطه‌ای برای نمایش نیست.</div>'
    : '<div id="map"></div>';

  return `<!DOCTYPE html>
<html><head>
<meta name="viewport" content="width=device-width, initial-scale=1">
${LEAFLET_TAGS}
<style>
  html,body,#map{height:100%;margin:0;padding:0}
  .empty{height:100%;display:flex;align-items:center;justify-content:center;
    font:14px system-ui,sans-serif;color:${esc(WEB_PALETTES.dark.ink2)};
    background:${esc(WEB_PALETTES.dark.surface)}}
  .pp{font:13px system-ui,sans-serif;line-height:1.5}
  .pp b{display:block}
  .pp small{opacity:.75}
</style>
</head><body>${body}<script>
  function out(msg) {
    var s = JSON.stringify(msg);
    if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(s);
    else if (window.parent && window.parent !== window) window.parent.postMessage(s, '*');
  }
  // خطای این صفحه هیچ کنسولی ندارد که کسی ببیندش؛ می‌رود بیرون تا خودِ
  // سربرگ بتواند بگوید «نقشه بالا نیامد» — و معلوم باشد کادر چرا خالی است.
  window.onerror = function (m, s, l) {
    out({ type: 'error', message: String(m) + ' @' + String(l) });
    return false;
  };

  // شعاع این‌جا از قبل حساب شده و داخلِ صفحه دوباره حساب نمی‌شود: قاعده‌اش
  // یک تصمیمِ نمایشی است و باید با npm test آزمودنی بماند، نه داخلِ رشته‌ای
  // که هیچ تستی اجرایش نمی‌کند. (بدونِ بک‌تیک: این کامنت خودش داخلِ یک
  // template literal است و بک‌تیک می‌بنددش.)
  var PTS = ${safeJson(usable.map((p) => ({
    lat: p.lat, lng: p.lng, t: p.title, n: p.note ?? '',
    e: p.events, s: p.suspect ? 1 : 0, r: dotRadius(p.events),
  })))};

  if (PTS.length) {
    if (!window.L) window.onerror('leaflet did not load', '', 0);

    var map = L.map('map', { scrollWheelZoom: false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '© OpenStreetMap'
    }).addTo(map);

    // متنِ پاپ‌آپ از دیتابیس می‌آید؛ با createTextNode می‌نشیند و نه با
    // innerHTML، پس نامِ مکانی که تگ داشته باشد تگ نمی‌شود.
    function popup(p) {
      var d = document.createElement('div');
      d.className = 'pp';
      var b = document.createElement('b');
      b.appendChild(document.createTextNode(p.t || '—'));
      d.appendChild(b);
      if (p.n) {
        var s = document.createElement('small');
        s.appendChild(document.createTextNode(p.n));
        d.appendChild(s);
        d.appendChild(document.createElement('br'));
      }
      d.appendChild(document.createTextNode(
        p.e + ' رویداد · ' + p.lat.toFixed(5) + ', ' + p.lng.toFixed(5)));
      return d;
    }

    var bounds = [];
    PTS.forEach(function (p) {
      L.circleMarker([p.lat, p.lng], {
        radius: p.r,
        color: p.s ? ${safeJson(colors.suspect)} : ${safeJson(colors.dot)},
        weight: 2, opacity: .9,
        fillColor: p.s ? ${safeJson(colors.suspect)} : ${safeJson(colors.dot)},
        fillOpacity: .35,
      }).addTo(map).bindPopup(popup(p));
      bounds.push([p.lat, p.lng]);
    });

    // یک نقطه مرزی ندارد که بشود رویش fit کرد — آن‌وقت Leaflet تا آخر زوم
    // می‌کند و آدم روی یک بامِ خاکستری می‌ماند.
    if (bounds.length === 1) map.setView(bounds[0], 14);
    else map.fitBounds(bounds, { padding: [24, 24] });
  }
</script></body></html>`;
}
